import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import RichTextEditor from '../components/RichTextEditor';
import PRTemplateSelector, { PR_TEMPLATES } from '../components/PRTemplateSelector';
import { toast } from 'sonner';
import { 
  Upload, X, Image, MapPin, Tag, Quote, Info, 
  Save, Clock, FileText, User, Building, Globe,
  ChevronDown, ChevronUp, Plus, Trash2, CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SubmitContent = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showTemplates, setShowTemplates] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Required fields
  const [title, setTitle] = useState('');
  const [pressReleaseContent, setPressReleaseContent] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [location, setLocation] = useState('');
  
  // Optional fields
  const [summary, setSummary] = useState('');
  const [prTag, setPrTag] = useState('');
  
  // Multiple Quotes
  const [quotes, setQuotes] = useState([
    { text: '', speakerName: '', speakerTitle: '', company: '' }
  ]);
  
  // Brand Information
  const [brandName, setBrandName] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [boilerplate, setBoilerplate] = useState('');
  
  // Media Kit
  const [logoUrl, setLogoUrl] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  
  // Additional Information
  const [notes, setNotes] = useState('');
  const [preferredPublishDate, setPreferredPublishDate] = useState('');
  const [links, setLinks] = useState([{ url: '', anchor: '' }]);
  
  // Expanded sections
  const [expandedSections, setExpandedSections] = useState({
    template: true,
    content: true,
    images: true,
    quotes: false,
    brand: false,
    links: false,
    additional: false
  });

  // Auto-save draft to localStorage
  const saveDraft = useCallback(() => {
    const draft = {
      title, pressReleaseContent, summary, location, prTag,
      quotes, brandName, websiteUrl, boilerplate, logoUrl,
      notes, preferredPublishDate, links, uploadedImages,
      selectedTemplate: selectedTemplate?.id,
      savedAt: new Date().toISOString()
    };
    localStorage.setItem(`pr_draft_${orderId}`, JSON.stringify(draft));
    setLastSaved(new Date());
    setIsDirty(false);
  }, [orderId, title, pressReleaseContent, summary, location, prTag, quotes, brandName, websiteUrl, boilerplate, logoUrl, notes, preferredPublishDate, links, uploadedImages, selectedTemplate]);

  // Load draft from localStorage
  const loadDraft = useCallback(() => {
    const savedDraft = localStorage.getItem(`pr_draft_${orderId}`);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        setTitle(draft.title || '');
        setPressReleaseContent(draft.pressReleaseContent || '');
        setSummary(draft.summary || '');
        setLocation(draft.location || '');
        setPrTag(draft.prTag || '');
        setQuotes(draft.quotes || [{ text: '', speakerName: '', speakerTitle: '', company: '' }]);
        setBrandName(draft.brandName || '');
        setWebsiteUrl(draft.websiteUrl || '');
        setBoilerplate(draft.boilerplate || '');
        setLogoUrl(draft.logoUrl || '');
        setNotes(draft.notes || '');
        setPreferredPublishDate(draft.preferredPublishDate || '');
        setLinks(draft.links || [{ url: '', anchor: '' }]);
        setUploadedImages(draft.uploadedImages || []);
        if (draft.selectedTemplate) {
          const template = PR_TEMPLATES.find(t => t.id === draft.selectedTemplate);
          setSelectedTemplate(template || null);
        }
        setLastSaved(new Date(draft.savedAt));
        toast.success('Draft restored');
        return true;
      } catch (e) {
        console.error('Error loading draft:', e);
      }
    }
    return false;
  }, [orderId]);

  // Auto-save every 30 seconds when dirty
  useEffect(() => {
    if (!isDirty) return;
    
    const timer = setTimeout(() => {
      saveDraft();
      toast.success('Draft auto-saved', { duration: 2000 });
    }, 30000);
    
    return () => clearTimeout(timer);
  }, [isDirty, saveDraft]);

  // Mark as dirty on any change
  useEffect(() => {
    setIsDirty(true);
  }, [title, pressReleaseContent, summary, location, prTag, quotes, brandName, websiteUrl, boilerplate, notes, preferredPublishDate, links, uploadedImages]);

  useEffect(() => {
    fetchOrder();
  }, [orderId]);

  const fetchOrder = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }

    try {
      const response = await axios.get(`${API}/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrder(response.data.order);
      
      if (response.data.order.payment_status !== 'paid') {
        toast.error('Order not paid');
        navigate('/marketplace');
        return;
      }
      
      // Try to load draft after order is loaded
      loadDraft();
    } catch (error) {
      console.error('Error fetching order:', error);
      toast.error('Failed to load order');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    if (template.id !== 'blank') {
      setTitle(template.title);
      setPressReleaseContent(template.content);
      setShowTemplates(false);
      toast.success(`${template.name} template applied`);
    } else {
      setTitle('');
      setPressReleaseContent('');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Quote management
  const addQuote = () => {
    setQuotes([...quotes, { text: '', speakerName: '', speakerTitle: '', company: '' }]);
  };

  const updateQuote = (index, field, value) => {
    const newQuotes = [...quotes];
    newQuotes[index][field] = value;
    setQuotes(newQuotes);
  };

  const removeQuote = (index) => {
    if (quotes.length > 1) {
      setQuotes(quotes.filter((_, i) => i !== index));
    }
  };

  // Link management
  const addLink = () => {
    setLinks([...links, { url: '', anchor: '' }]);
  };

  const updateLink = (index, field, value) => {
    const newLinks = [...links];
    newLinks[index][field] = value;
    setLinks(newLinks);
  };

  const removeLink = (index) => {
    if (links.length > 1) {
      setLinks(links.filter((_, i) => i !== index));
    }
  };

  // Image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const token = localStorage.getItem('token');
    setUploadingImages(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/content/upload-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        return {
          url: BACKEND_URL + response.data.url,
          name: file.name,
          caption: ''
        };
      });

      const newImages = await Promise.all(uploadPromises);
      setUploadedImages([...uploadedImages, ...newImages]);
      toast.success(`${files.length} image(s) uploaded`);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Failed to upload images');
    } finally {
      setUploadingImages(false);
    }
  };

  const updateImageCaption = (index, caption) => {
    const newImages = [...uploadedImages];
    newImages[index].caption = caption;
    setUploadedImages(newImages);
  };

  const removeImage = (index) => {
    setUploadedImages(uploadedImages.filter((_, i) => i !== index));
  };

  const setFeaturedImage = (index) => {
    const newImages = [...uploadedImages];
    const [featured] = newImages.splice(index, 1);
    newImages.unshift(featured);
    setUploadedImages(newImages);
    toast.success('Featured image set');
  };

  // Logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const token = localStorage.getItem('token');
    setUploadingLogo(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/content/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setLogoUrl(BACKEND_URL + response.data.url);
      toast.success('Logo uploaded');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!title.trim()) {
      toast.error('Title is required');
      return;
    }
    
    const hasWritingService = order?.add_ons?.writing_service;
    if (!hasWritingService && !pressReleaseContent.trim()) {
      toast.error('Press Release Content is required');
      return;
    }
    
    if (uploadedImages.length === 0) {
      toast.error('At least one image is required');
      return;
    }
    
    if (!location.trim()) {
      toast.error('Location is required');
      return;
    }

    const token = localStorage.getItem('token');

    try {
      setSubmitting(true);
      
      const submissionData = {
        order_id: orderId,
        title: title.trim(),
        content_text: pressReleaseContent,
        images: uploadedImages.map(img => img.url),
        image_captions: uploadedImages.map(img => img.caption || ''),
        city: location.trim(),
        summary: summary.trim() || null,
        pr_tag: prTag.trim() || null,
        quotes: quotes.filter(q => q.text.trim()),
        brand_name: brandName.trim() || null,
        website_url: websiteUrl.trim() || null,
        boilerplate: boilerplate.trim() || null,
        logo_url: logoUrl || null,
        links: links.filter(l => l.url.trim()),
        notes: notes.trim() || null,
        preferred_publish_date: preferredPublishDate || null,
        template_used: selectedTemplate?.id || null
      };

      await axios.post(
        `${API}/orders/${orderId}/submit-content`,
        submissionData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Clear draft on successful submission
      localStorage.removeItem(`pr_draft_${orderId}`);
      
      toast.success('Content submitted successfully!');
      navigate(`/order/${orderId}`);
    } catch (error) {
      console.error('Submission error:', error);
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-8"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const SectionHeader = ({ title, icon: Icon, section, required }) => (
    <button
      type="button"
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
        <h2 className="font-playfair text-lg font-bold text-[#1a1a1a]">
          {title}
          {required && <span className="text-red-500 ml-1">*</span>}
        </h2>
      </div>
      {expandedSections[section] ? (
        <ChevronUp className="w-5 h-5 text-gray-400" />
      ) : (
        <ChevronDown className="w-5 h-5 text-gray-400" />
      )}
    </button>
  );

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <Header />
      
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-2 text-[#1a1a1a]">
              Submit Press Release
            </h1>
            <p className="font-manrope text-sm text-[#737373]">
              Order ID: {orderId.substring(0, 8)}
            </p>
          </div>
          
          {/* Auto-save indicator */}
          <div className="flex items-center gap-4 mt-4 md:mt-0">
            {lastSaved && (
              <span className="flex items-center gap-2 text-sm text-[#737373]">
                <Clock className="w-4 h-4" />
                Last saved: {lastSaved.toLocaleTimeString()}
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                saveDraft();
                toast.success('Draft saved');
              }}
              className="flex items-center gap-2 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37] hover:text-white transition-colors text-sm"
            >
              <Save className="w-4 h-4" />
              Save Draft
            </button>
          </div>
        </div>
        
        {/* Required Fields Notice */}
        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5" />
            <div>
              <p className="font-manrope text-sm font-semibold text-[#1a1a1a]">Required for submission:</p>
              <p className="font-manrope text-sm text-[#737373]">Title, Press Release Content, At least 1 Image, and Location</p>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* PR Templates Section */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Choose a Template" icon={FileText} section="template" />
            {expandedSections.template && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <p className="font-manrope text-sm text-[#737373] mb-4">
                  Select a template to get started quickly with a pre-formatted structure
                </p>
                <PRTemplateSelector 
                  onSelect={handleTemplateSelect} 
                  selectedTemplate={selectedTemplate} 
                />
              </div>
            )}
          </div>

          {/* Title */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-6">
            <label className="flex items-center gap-2 font-playfair text-lg font-bold text-[#1a1a1a] mb-4">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="title-input"
              placeholder="Enter a compelling headline for your press release..."
              className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-base focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* Summary */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-6">
            <label className="font-playfair text-lg font-bold text-[#1a1a1a] mb-2 block">Summary</label>
            <p className="font-manrope text-xs text-[#737373] mb-4">Optional - A brief 2-3 sentence summary</p>
            <textarea
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              data-testid="summary-textarea"
              rows="2"
              placeholder="Brief summary for editors and quick scanning..."
              className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* Rich Text Content */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Press Release Content" icon={FileText} section="content" required />
            {expandedSections.content && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <p className="font-manrope text-xs text-[#737373] mb-4">
                  Use the formatting toolbar to add headings, lists, quotes, and links
                </p>
                <RichTextEditor
                  content={pressReleaseContent}
                  onChange={setPressReleaseContent}
                  placeholder="Start writing your press release content... Use headings, bullet points, and quotes to structure your content."
                />
              </div>
            )}
          </div>

          {/* Images */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Images" icon={Image} section="images" required />
            {expandedSections.images && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <p className="font-manrope text-xs text-[#737373] mb-4">
                  Upload high-resolution images. The first image will be used as the featured image.
                </p>
                
                <div className="mb-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    disabled={uploadingImages}
                    data-testid="image-upload-input"
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-[#D4AF37] text-[#D4AF37] rounded-lg font-manrope text-xs uppercase tracking-widest hover:bg-[#D4AF37] hover:text-white cursor-pointer transition-all duration-300"
                  >
                    <Upload className="w-4 h-4" />
                    {uploadingImages ? 'Uploading...' : 'Upload Images'}
                  </label>
                </div>

                {uploadedImages.length > 0 ? (
                  <div className="space-y-4">
                    {uploadedImages.map((img, index) => (
                      <div key={index} className="flex gap-4 p-4 border border-[#e5e5e5] rounded-lg">
                        <div className="relative w-32 h-24 flex-shrink-0">
                          <img
                            src={img.url}
                            alt={img.name}
                            className="w-full h-full object-cover rounded-lg"
                          />
                          {index === 0 && (
                            <span className="absolute top-1 left-1 px-2 py-0.5 bg-[#D4AF37] text-white text-xs rounded">
                              Featured
                            </span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-manrope text-sm font-medium text-[#1a1a1a] mb-2">{img.name}</p>
                          <input
                            type="text"
                            value={img.caption || ''}
                            onChange={(e) => updateImageCaption(index, e.target.value)}
                            placeholder="Add caption (optional)"
                            className="w-full border border-[#e5e5e5] rounded px-3 py-2 text-sm focus:ring-1 focus:ring-[#D4AF37] focus:outline-none"
                          />
                        </div>
                        <div className="flex flex-col gap-2">
                          {index !== 0 && (
                            <button
                              type="button"
                              onClick={() => setFeaturedImage(index)}
                              className="p-2 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded"
                              title="Set as featured"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded"
                            title="Remove"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-red-500">At least one image is required</p>
                )}
              </div>
            )}
          </div>

          {/* Location */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-6">
            <label className="flex items-center gap-2 font-playfair text-lg font-bold text-[#1a1a1a] mb-4">
              <MapPin className="w-5 h-5 text-[#D4AF37]" />
              Location <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              data-testid="location-input"
              placeholder="e.g., Mumbai, India"
              className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* PR Tag */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg p-6">
            <label className="flex items-center gap-2 font-playfair text-lg font-bold text-[#1a1a1a] mb-2">
              <Tag className="w-5 h-5 text-[#D4AF37]" />
              PR Tag
            </label>
            <p className="font-manrope text-xs text-[#737373] mb-4">Optional - Category for your press release</p>
            <input
              type="text"
              value={prTag}
              onChange={(e) => setPrTag(e.target.value)}
              data-testid="pr-tag-input"
              placeholder="e.g., Product Launch, Funding, Partnership"
              className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
            />
          </div>

          {/* Quotes Section */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Quotes" icon={Quote} section="quotes" />
            {expandedSections.quotes && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <p className="font-manrope text-xs text-[#737373] mb-4">
                  Add quotes from spokespersons with proper attribution
                </p>
                
                {quotes.map((quote, index) => (
                  <div key={index} className="p-4 border border-[#e5e5e5] rounded-lg mb-4">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-manrope text-sm font-medium text-[#1a1a1a]">Quote {index + 1}</span>
                      {quotes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeQuote(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    <textarea
                      value={quote.text}
                      onChange={(e) => updateQuote(index, 'text', e.target.value)}
                      rows="3"
                      placeholder="Enter the quote text..."
                      className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none mb-3"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={quote.speakerName}
                        onChange={(e) => updateQuote(index, 'speakerName', e.target.value)}
                        placeholder="Speaker Name"
                        className="border border-[#e5e5e5] rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                      />
                      <input
                        type="text"
                        value={quote.speakerTitle}
                        onChange={(e) => updateQuote(index, 'speakerTitle', e.target.value)}
                        placeholder="Title (e.g., CEO)"
                        className="border border-[#e5e5e5] rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                      />
                      <input
                        type="text"
                        value={quote.company}
                        onChange={(e) => updateQuote(index, 'company', e.target.value)}
                        placeholder="Company Name"
                        className="border border-[#e5e5e5] rounded-lg px-3 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={addQuote}
                  className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/5 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Quote
                </button>
              </div>
            )}
          </div>

          {/* Brand Information */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Brand Information" icon={Building} section="brand" />
            {expandedSections.brand && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <p className="font-manrope text-xs text-[#737373] mb-4">
                  All fields are optional. This information helps create a complete press release.
                </p>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Brand Name</label>
                      <input
                        type="text"
                        value={brandName}
                        onChange={(e) => setBrandName(e.target.value)}
                        placeholder="Your company name"
                        className="w-full border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Website URL</label>
                      <input
                        type="url"
                        value={websiteUrl}
                        onChange={(e) => setWebsiteUrl(e.target.value)}
                        placeholder="https://yourcompany.com"
                        className="w-full border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Brand Logo</label>
                    <div className="flex items-center gap-4">
                      {logoUrl ? (
                        <div className="relative">
                          <img src={logoUrl} alt="Logo" className="w-20 h-20 object-contain border border-[#e5e5e5] rounded-lg p-2" />
                          <button
                            type="button"
                            onClick={() => setLogoUrl('')}
                            className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleLogoUpload}
                            disabled={uploadingLogo}
                            className="hidden"
                            id="logo-upload"
                          />
                          <label
                            htmlFor="logo-upload"
                            className="flex items-center gap-2 px-4 py-2 border border-[#e5e5e5] rounded-lg cursor-pointer hover:border-[#D4AF37] transition-colors text-sm"
                          >
                            <Upload className="w-4 h-4" />
                            {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Company Boilerplate</label>
                    <textarea
                      value={boilerplate}
                      onChange={(e) => setBoilerplate(e.target.value)}
                      rows="3"
                      placeholder="Standard 'About Company' paragraph used at the end of press releases..."
                      className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Links */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Links & Anchor Text" icon={Globe} section="links" />
            {expandedSections.links && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <p className="font-manrope text-xs text-[#737373] mb-4">
                  Add links to include in your article with custom anchor text
                </p>
                
                {links.map((link, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 mb-3">
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => updateLink(index, 'url', e.target.value)}
                      placeholder="https://..."
                      className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                    />
                    <input
                      type="text"
                      value={link.anchor}
                      onChange={(e) => updateLink(index, 'anchor', e.target.value)}
                      placeholder="Anchor text"
                      className="border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                    />
                    {links.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLink(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                ))}
                
                <button
                  type="button"
                  onClick={addLink}
                  className="flex items-center gap-2 px-4 py-2 border border-dashed border-[#D4AF37] text-[#D4AF37] rounded-lg hover:bg-[#D4AF37]/5 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Link
                </button>
              </div>
            )}
          </div>

          {/* Additional Information */}
          <div className="bg-white border border-[#e5e5e5] rounded-lg overflow-hidden">
            <SectionHeader title="Additional Information" icon={Info} section="additional" />
            {expandedSections.additional && (
              <div className="p-4 border-t border-[#e5e5e5]">
                <div className="space-y-4">
                  <div>
                    <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Preferred Publish Date</label>
                    <input
                      type="date"
                      value={preferredPublishDate}
                      onChange={(e) => setPreferredPublishDate(e.target.value)}
                      className="w-full border border-[#e5e5e5] rounded-lg px-4 py-2 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                  
                  <div>
                    <label className="font-manrope text-xs text-[#737373] mb-2 block uppercase tracking-widest">Notes for Editor</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows="3"
                      placeholder="Any special requirements, formatting preferences, or notes for the editorial team..."
                      className="w-full border border-[#e5e5e5] rounded-lg px-4 py-3 font-manrope text-sm focus:ring-2 focus:ring-[#D4AF37]/20 focus:border-[#D4AF37] focus:outline-none"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4">
            <button
              type="button"
              onClick={() => {
                saveDraft();
                toast.success('Draft saved');
              }}
              className="flex-1 px-8 py-4 border border-[#D4AF37] text-[#D4AF37] rounded-lg font-manrope text-sm uppercase tracking-widest hover:bg-[#D4AF37]/5 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              Save Draft
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-content-button"
              className="flex-1 px-8 py-4 bg-[#D4AF37] text-[#0d0d0d] rounded-lg font-manrope text-sm uppercase tracking-widest font-semibold hover:bg-[#B8960F] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Submitting...
                </>
              ) : (
                <>Submit Press Release</>
              )}
            </button>
          </div>
        </form>
      </div>
      
      <Footer />
    </div>
  );
};

export default SubmitContent;
